import React, { useMemo, useState } from "react";
import { MaterialSearchPicker } from "../MaterialSearchPicker";
import { CPInput } from "../CPInput";
import { InlineRadioGroup } from "../InlineRadioGroup";
import { usePermissions } from "../../hooks/usePermissions";
import {
  addWorkTypeLabour,
  CATALOG_MATERIAL_TYPE,
  CATALOG_OTHER_SERVICE,
  filterCatalogByMaterialType,
  LABOUR_PRICING_METHOD,
  updateWorkTypeLabour,
} from "../../helpers/estimates";
import { computeWorkTypeTotalSqft } from "../../helpers/estimateTotals";
import { EstimateBottomSheet } from "./EstimateBottomSheet";

const LABOUR_PRICING_OPTIONS = [
  { value: LABOUR_PRICING_METHOD.HOURLY, label: "Hourly" },
  { value: LABOUR_PRICING_METHOD.SQUARE_FOOT, label: "Square Foot" },
];

function isHourlyPricing(method) {
  return (method || LABOUR_PRICING_METHOD.HOURLY) === LABOUR_PRICING_METHOD.HOURLY;
}

function labourFormFromRow(row) {
  return {
    labourActivity: row.labour_activity || "",
    estimatedHours:
      row.estimated_hours == null || row.estimated_hours === ""
        ? ""
        : String(row.estimated_hours),
    hourlyRate:
      row.hourly_rate == null || row.hourly_rate === "" ? "" : String(row.hourly_rate),
    notes: row.notes || "",
    pricingMethod: row.pricing_method || LABOUR_PRICING_METHOD.HOURLY,
  };
}

/**
 * Focused Add/Edit Labour entry surface — preserves catalog service / Other
 * Service / Hourly / Square Foot pricing exactly as the previous inline
 * panel. Reuses addWorkTypeLabour/updateWorkTypeLabour unchanged. Delete
 * remains a separate hard-delete action (not handled here) — this task does
 * not normalize that behavior.
 */
export function LabourFormSheet({
  workType,
  materialsCatalog = [],
  row = null,
  onClose,
  onSaved,
}) {
  const { actor } = usePermissions();
  const servicesCatalog = useMemo(
    () => filterCatalogByMaterialType(materialsCatalog, CATALOG_MATERIAL_TYPE.SERVICE),
    [materialsCatalog],
  );

  const isEditing = Boolean(row);
  const [serviceId, setServiceId] = useState("");
  const [pricingMethod, setPricingMethod] = useState(
    row?.pricing_method || LABOUR_PRICING_METHOD.HOURLY,
  );
  const [hours, setHours] = useState(
    row ? labourFormFromRow(row).estimatedHours : "",
  );
  const [rate, setRate] = useState(row ? labourFormFromRow(row).hourlyRate : "");
  const [notes, setNotes] = useState(row?.notes || "");
  const [activity, setActivity] = useState(row?.labour_activity || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const isOtherService = serviceId === CATALOG_OTHER_SERVICE;
  const isHourly = isHourlyPricing(pricingMethod);
  const selectedService = servicesCatalog.find(
    (item) => item.material_id === serviceId,
  );

  function handleServiceChange(value) {
    setServiceId(value);
    if (value === CATALOG_OTHER_SERVICE) {
      setRate("");
      return;
    }
    const service = servicesCatalog.find((item) => item.material_id === value);
    if (service) {
      setRate(
        service.unit_cost == null || service.unit_cost === ""
          ? ""
          : String(service.unit_cost),
      );
    }
  }

  function handlePricingMethodChange(method) {
    setPricingMethod(method);
    if (!isHourlyPricing(method)) setHours("");
  }

  function canSubmit() {
    if (isEditing) return true;
    if (isOtherService) {
      if (!activity.trim()) return false;
      return isHourly ? Boolean(hours) : true;
    }
    if (!selectedService) return false;
    return isHourly ? Boolean(hours) : true;
  }

  async function handleSave() {
    setError("");

    if (isEditing) {
      setSaving(true);
      try {
        await updateWorkTypeLabour(actor, row.labour_id, {
          labourActivity: activity,
          estimatedHours: isHourly ? hours : "",
          hourlyRate: rate,
          notes,
          pricingMethod,
        });
        await onSaved?.();
        onClose?.();
      } catch (err) {
        setError(err.message || "Unable to update labour.");
      } finally {
        setSaving(false);
      }
      return;
    }

    if (!canSubmit()) {
      setError("Select a service and enter the required details.");
      return;
    }

    setSaving(true);
    try {
      await addWorkTypeLabour(actor, workType.estimate_feature_id, {
        labourActivity: isOtherService ? activity : selectedService.material_name,
        estimatedHours: isHourly ? hours : null,
        hourlyRate: rate,
        notes,
        pricingMethod,
      });
      await onSaved?.();
      onClose?.();
    } catch (err) {
      setError(err.message || "Unable to add labour.");
    } finally {
      setSaving(false);
    }
  }

  const sqft = computeWorkTypeTotalSqft(workType);

  return (
    <EstimateBottomSheet
      title={isEditing ? "Edit Labour" : "Add Labour"}
      onClose={onClose}
      onPrimary={handleSave}
      primaryLabel={isEditing ? "Save Changes" : "Add Labour"}
      primaryDisabled={!canSubmit()}
      primaryBusy={saving}
      error={error}
    >
      {!isEditing && (
        <MaterialSearchPicker
          materials={servicesCatalog}
          value={serviceId}
          onChange={handleServiceChange}
          disabled={saving}
          label="Service"
          placeholder="Search Services..."
          otherOptionLabel="Other Service..."
          otherOptionValue={CATALOG_OTHER_SERVICE}
          noResultsLabel="No catalog services found."
        />
      )}

      {(isEditing || isOtherService) && (
        <CPInput
          label={isOtherService ? "Service Name" : "Activity"}
          value={activity}
          onChange={setActivity}
          required={isOtherService}
        />
      )}

      <InlineRadioGroup
        name="labour-pricing-method"
        value={pricingMethod}
        onChange={handlePricingMethodChange}
        options={LABOUR_PRICING_OPTIONS}
        disabled={saving}
      />

      {isHourly ? (
        <>
          <CPInput
            label="Hours"
            value={hours}
            onChange={setHours}
            type="number"
            required
          />
          <CPInput label="Rate (optional)" value={rate} onChange={setRate} type="number" />
        </>
      ) : (
        <>
          {sqft > 0 ? (
            <div className="est-feature-summary-sqft">
              Measured area: {sqft.toFixed(1)} ft²
            </div>
          ) : (
            <div className="est-feature-summary-sqft">
              Add measurements to price by square foot.
            </div>
          )}
          <CPInput
            label="Rate per sq ft (optional)"
            value={rate}
            onChange={setRate}
            type="number"
          />
        </>
      )}

      <CPInput label="Notes (optional)" value={notes} onChange={setNotes} />
    </EstimateBottomSheet>
  );
}
